/**
 * The agent's turn lifecycle (F-144), shared by the panel and by "fill this
 * new board from a file" (`pending-import.ts`): send a message, apply a
 * proposed plan, revert an applied one. Each step updates the UI store, so a
 * caller with no panel on screen (the on-open import) leaves the same
 * transcript a person would have produced by hand.
 *
 * Takes a NARROW view of `BoardSession` (`AgentSession`) — what the applier
 * writes through plus the four things a turn reads — so the whole lifecycle
 * is testable with a fake session over a bare `BoardDoc`.
 */
import type {
  AgentAttachment,
  AgentSelection,
  ImportStepTask,
  BoardDoc,
  CellAddress,
  CellRangeSelection,
  CellScalar,
  ColumnType,
} from "@kola/shared";

import { ApiError, askBoardAgent, runImportStep as askImportStep } from "../../core/api.js";
import { isMac } from "../../core/keyboard.js";
import { showToast } from "../../ui/toast.js";
import { applyAgentPlan, revertAgentRun, type AgentWriter, type AppliedRun, type ApplySkips } from "./apply.js";
import { agentErrorMessage } from "./errors.js";
import { buildHistory } from "./history.js";
import {
  IMPORT_STEP_MESSAGES,
  freshImportTasks,
  groupsFromRun,
  saveImportTasks,
  withStepDone,
  withStepPending,
  type ImportTasks,
} from "./import-tasks.js";
import type { AgentTurn, AgentUiStore } from "./store.js";

/** The slice of `BoardSession` a turn needs. */
export interface AgentSession {
  readonly boardId: string;
  readonly board: BoardDoc;
  readonly store: {
    getState(): {
      sheetId: string | null;
      frozen: boolean;
      localSelection: CellRangeSelection | null;
      peers: readonly { editingCell?: CellAddress | undefined }[];
    };
  };
  whenFlushed(): Promise<void>;
  batch(fn: () => void): void;
  beginUndoGroup(): () => void;
  insertRows(sheetId: string, count: number, index?: number): string[];
  insertNamedColumn(
    sheetId: string,
    name: string,
    options?: { index?: number | undefined; type?: ColumnType | undefined },
  ): string | null;
  setCellValue(sheetId: string, rowId: string, colId: string, value: CellScalar): void;
  deleteRows(sheetId: string, rowIds: readonly string[]): void;
  deleteColumn(sheetId: string, colId: string): void;
  setColumnName(sheetId: string, colId: string, name: string): boolean;
}

export interface SendTurnInput {
  message: string;
  attachments?: readonly AgentAttachment[] | undefined;
  selection?: AgentSelection | undefined;
  /** `""` = the function default. */
  model?: string | undefined;
}

let turnSeq = 0;
export function turnId(): string {
  turnSeq += 1;
  return `t${Date.now().toString(36)}-${turnSeq}`;
}

/**
 * One turn: the user message goes into the transcript, the server is asked
 * (after the local edits have flushed, so it reads what was just typed), and
 * the assistant's answer — or the error, as an error turn — follows. Returns
 * the assistant turn. Never throws for a server/network failure.
 */
export async function sendAgentTurn(
  session: AgentSession,
  ui: AgentUiStore,
  input: SendTurnInput,
): Promise<AgentTurn> {
  const { sheetId } = session.store.getState();
  const files = input.attachments ?? [];
  const history = buildHistory(ui.getState().turns);
  ui.getState().pushTurn({
    id: turnId(),
    role: "user",
    text: input.message,
    ...(files.length === 0 ? {} : { attachments: files.map((f) => ({ name: f.name, chars: f.text.length })) }),
  });
  ui.getState().setPending(true);
  let turn: AgentTurn;
  try {
    if (sheetId === null) throw new Error("no sheet");
    await session.whenFlushed();
    const response = await askBoardAgent(session.boardId, {
      sheetId,
      message: input.message,
      ...(files.length === 0 ? {} : { attachments: [...files] }),
      ...(input.selection === undefined ? {} : { selection: input.selection }),
      ...(history.length === 0 ? {} : { history }),
      ...(input.model === undefined || input.model === "" ? {} : { model: input.model }),
    });
    turn = {
      id: turnId(),
      role: "assistant",
      text: response.reply,
      model: response.model,
      ...(response.plan === undefined ? {} : { plan: response.plan, planState: "proposed" }),
    };
  } catch (error) {
    const text =
      error instanceof ApiError
        ? agentErrorMessage(error.status, error.code, modelFromError(error))
        : "Couldn't reach the agent. Try again.";
    turn = { id: turnId(), role: "assistant", text, error: true };
  } finally {
    ui.getState().setPending(false);
  }
  ui.getState().pushTurn(turn);
  return turn;
}

/** Apply a proposed turn's plan. Resolves true when it landed. */
export async function applyTurn(session: AgentSession, ui: AgentUiStore, turnIdToApply: string): Promise<boolean> {
  const turn = ui.getState().turns.find((t) => t.id === turnIdToApply);
  if (turn === undefined || turn.plan === undefined || turn.planState !== "proposed") return false;
  ui.getState().updateTurn(turn.id, { planState: "applying" });
  const outcome = await applyAgentPlan(writerFor(session), turn.plan);
  if (!outcome.ok) {
    ui.getState().updateTurn(turn.id, { planState: "proposed" });
    showToast(blockedMessage(outcome.reason));
    return false;
  }
  ui.getState().setRun(outcome.run);
  ui.getState().updateTurn(turn.id, { planState: "applied", runId: outcome.run.id });
  showToast(appliedToast(outcome.run), { durationMs: 6000 });
  // F-150: the import landed → its step tasks appear; a step landed → its
  // button goes (the split leaving behind the groups "Align" works on).
  const tasks = ui.getState().importTasks;
  if (turn.importFile !== undefined) {
    setImportTasks(session, ui, freshImportTasks(turn.importFile, turn.plan.sheetId));
  } else if (turn.importStep !== undefined && tasks !== null) {
    const groups = turn.importStep === "beats" ? groupsFromRun(turn.plan, outcome.run) : undefined;
    setImportTasks(session, ui, withStepDone(tasks, turn.importStep, groups));
  }
  return true;
}

/** Revert an applied turn's run. Resolves true when it was reverted. */
export async function revertTurn(session: AgentSession, ui: AgentUiStore, turnIdToRevert: string): Promise<boolean> {
  const turn = ui.getState().turns.find((t) => t.id === turnIdToRevert);
  const run = turn?.runId === undefined ? undefined : ui.getState().runs[turn.runId];
  if (turn === undefined || run === undefined || turn.planState !== "applied") return false;
  ui.getState().updateTurn(turn.id, { planState: "reverting" });
  const outcome = await revertAgentRun(writerFor(session), run);
  if (!outcome.ok) {
    ui.getState().updateTurn(turn.id, { planState: "applied" });
    showToast(blockedMessage(outcome.reason));
    return false;
  }
  ui.getState().updateTurn(turn.id, { planState: "reverted" });
  showToast(`Reverted.${skipsSuffix(outcome.skipped)}`, { durationMs: 6000 });
  // F-150: an undone import takes its tasks with it; an undone step is a
  // step to do again.
  const tasks = ui.getState().importTasks;
  if (turn.importFile !== undefined) setImportTasks(session, ui, null);
  else if (turn.importStep !== undefined && tasks !== null) {
    setImportTasks(session, ui, withStepPending(tasks, turn.importStep));
  }
  return true;
}

/* ----------------------------- import steps ----------------------------- */

function setImportTasks(session: AgentSession, ui: AgentUiStore, tasks: ImportTasks | null): void {
  ui.getState().setImportTasks(tasks);
  saveImportTasks(session.boardId, tasks);
}

/** F-150: the panel's "Dismiss" — the remaining steps are not wanted. */
export function dismissImportTasks(session: AgentSession, ui: AgentUiStore): void {
  setImportTasks(session, ui, null);
}

/**
 * F-150: run one step task — the person's ask goes into the transcript, the
 * server proposes the step as a plan, and the answer follows as an
 * assistant turn the person applies (or discards) like any other. A step
 * with nothing to do (no row to split, nothing to divide) is done there and
 * then; a translation that came back empty is not. Never throws.
 */
export async function runImportStep(session: AgentSession, ui: AgentUiStore, step: ImportStepTask): Promise<AgentTurn | undefined> {
  const tasks = ui.getState().importTasks;
  if (tasks === null || ui.getState().pending) return undefined;
  ui.getState().pushTurn({ id: turnId(), role: "user", text: IMPORT_STEP_MESSAGES[step] });
  ui.getState().setPending(true);
  let turn: AgentTurn;
  try {
    await session.whenFlushed();
    const response = await askImportStep(session.boardId, {
      step,
      sheetId: tasks.sheetId,
      ...(step === "align" && tasks.groups !== undefined ? { groups: tasks.groups } : {}),
    });
    turn = {
      id: turnId(),
      role: "assistant",
      text: response.reply,
      model: response.model,
      importStep: step,
      ...(response.plan === undefined ? {} : { plan: response.plan, planState: "proposed" }),
    };
    if (response.plan === undefined && step !== "translate") {
      const current = ui.getState().importTasks;
      if (current !== null) setImportTasks(session, ui, withStepDone(current, step));
    }
  } catch (error) {
    const text =
      error instanceof ApiError
        ? agentErrorMessage(error.status, error.code, modelFromError(error))
        : "Couldn't reach the agent. Try again.";
    turn = { id: turnId(), role: "assistant", text, error: true };
  } finally {
    ui.getState().setPending(false);
  }
  ui.getState().pushTurn(turn);
  return turn;
}

/* ------------------------------- helpers -------------------------------- */

/** The session as the applier sees it — every write local-origin, F-42 honoured. */
export function writerFor(session: AgentSession): AgentWriter {
  return {
    board: session.board,
    batch: (fn) => session.batch(fn),
    beginUndoGroup: () => session.beginUndoGroup(),
    insertRows: (sheetId, count, index) => session.insertRows(sheetId, count, index),
    insertNamedColumn: (sheetId, name, options) => session.insertNamedColumn(sheetId, name, options),
    setCellValue: (sheetId, rowId, colId, value) => session.setCellValue(sheetId, rowId, colId, value),
    deleteRows: (sheetId, rowIds) => session.deleteRows(sheetId, rowIds),
    deleteColumn: (sheetId, colId) => session.deleteColumn(sheetId, colId),
    setColumnName: (sheetId, colId, name) => session.setColumnName(sheetId, colId, name),
    isCellClaimed: (sheetId, rowId, colId) =>
      session.store
        .getState()
        .peers.some(
          (peer) =>
            peer.editingCell !== undefined &&
            peer.editingCell.sheetId === sheetId &&
            peer.editingCell.rowId === rowId &&
            peer.editingCell.colId === colId,
        ),
  };
}

function blockedMessage(reason: "frozen" | "sheet-missing"): string {
  return reason === "frozen"
    ? "The board is read-only right now — reconnect and try again."
    : "That sheet no longer exists.";
}

export function appliedToast(run: AppliedRun): string {
  const parts: string[] = [];
  const { counts } = run;
  const n = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;
  if (counts.rowsAdded > 0) parts.push(`${n(counts.rowsAdded, "row", "rows")} added`);
  if (counts.rowsDeleted > 0) parts.push(`${n(counts.rowsDeleted, "row", "rows")} deleted`);
  if (counts.columnsAdded > 0) parts.push(`${n(counts.columnsAdded, "column", "columns")} added`);
  if (counts.columnsDeleted > 0) parts.push(`${n(counts.columnsDeleted, "column", "columns")} deleted`);
  if (counts.columnsRenamed > 0) parts.push(`${counts.columnsRenamed} renamed`);
  if (counts.cells > 0) parts.push(`${n(counts.cells, "cell", "cells")} written`);
  const head = parts.length === 0 ? "Applied." : `Applied: ${parts.join(", ")}.`;
  return `${head}${skipsSuffix(run.skipped)} ${isMac ? "⌘Z" : "Ctrl+Z"} undoes it.`;
}

export function skipsSuffix(skipped: ApplySkips): string {
  const parts: string[] = [];
  const n = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`;
  if (skipped.claimed > 0) {
    parts.push(`${n(skipped.claimed, "cell", "cells")} skipped — someone is editing ${skipped.claimed === 1 ? "it" : "them"}`);
  }
  if (skipped.changed > 0) {
    parts.push(`${n(skipped.changed, "cell", "cells")} left as someone changed ${skipped.changed === 1 ? "it" : "them"} since`);
  }
  if (skipped.blockedRows > 0) parts.push(`${n(skipped.blockedRows, "row", "rows")} kept — edited since`);
  if (skipped.blockedColumns > 0) parts.push(`${n(skipped.blockedColumns, "column", "columns")} kept — edited since`);
  if (skipped.missing > 0) parts.push(`${n(skipped.missing, "target", "targets")} no longer there`);
  return parts.length === 0 ? "" : ` (${parts.join("; ")})`;
}

function modelFromError(failure: ApiError): string | undefined {
  const body = failure.body;
  if (typeof body !== "object" || body === null) return undefined;
  const model = (body as { model?: unknown }).model;
  return typeof model === "string" ? model : undefined;
}
